// Stage 200: GPP Laughter Module — Settings tab.
//
// Operator-only. Renders:
//   - Header + short explainer (what Laughter is, what first-pass does).
//   - Healthcheck banner.
//   - Enable switch (laughter_enabled).
//   - Accumulated-engrams counter card (total + per-subclass + last-fired).
//   - Recent engrams inspector (collapsible rows; full Shape-Q payload on expand).
//   - Pinned horizons panel (Stage 2 / Stage 3 / Stage X).
//
// NO history, NO regenerate, NO delete, NO export — Laughter is
// forward-only by design (Position α, canonical TINS). Operators who
// must manually expunge a row do so out-of-band.

import { useCallback, useEffect, useState } from 'react';
import { apiGet, apiJson } from '../api/client';
import {
  laughterHealthcheck,
  laughterCount,
  laughterList,
  type CountResponse,
  type LaughterHealth,
  type StoredLaughterEngram,
} from '../api/laughter';
import type { LaughterSubclass, ReframeSubject } from '../modules/Laughter';

interface SettingsResponse {
  settings: Record<string, string>;
}

const SUBCLASS_LABEL: Record<LaughterSubclass, string> = {
  'omission-laugh': 'Omission-laugh (OBSERVATION accepted)',
  'ambiguity-laugh': 'Ambiguity-laugh (QUESTION accepted)',
  'contradiction-laugh': 'Contradiction-laugh (OBJECTION accepted)',
};

// Stage 201-S: header label + snippet for any engram, subject-aware.
// Witness / joint engrams carry no Gadfly bite, so their header reads
// from the witnessReframe payload instead of acceptedBite / subclass.
function engramLabel(e: StoredLaughterEngram): string {
  const subject: ReframeSubject = e.reframeSubject ?? 'self';
  if (subject === 'witness') return 'Witness-reframe';
  if (subject === 'joint') return 'Joint-reframe';
  return e.subclass ? SUBCLASS_LABEL[e.subclass] : 'Self-reframe';
}

function engramSnippet(e: StoredLaughterEngram): string {
  const subject: ReframeSubject = e.reframeSubject ?? 'self';
  if (subject !== 'self') {
    return truncate(e.witnessReframe?.userMessage ?? e.userMessage ?? '', 80);
  }
  return truncate(e.acceptedBite?.text ?? '', 80);
}

function truncate(s: string, n: number): string {
  if (s.length <= n) return s;
  return s.slice(0, n - 1) + '…';
}

export function LaughterTab({ onChanged }: { onChanged: () => void }) {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  // Stage 201: the three horizon sub-stage flags.
  const [warpMarker, setWarpMarker] = useState<boolean>(false);
  const [correlations, setCorrelations] = useState<boolean>(false);
  const [witness, setWitness] = useState<boolean>(false);
  // Stage 202: the three deep-detector / read-back flags.
  const [semanticWitness, setSemanticWitness] = useState<boolean>(false);
  const [sharedGround, setSharedGround] = useState<boolean>(false);
  const [readback, setReadback] = useState<boolean>(false);
  const [counts, setCounts] = useState<CountResponse | null>(null);
  const [recent, setRecent] = useState<StoredLaughterEngram[]>([]);
  // Stage 201-S: optional reframe-subject filter for the inspector list.
  const [subjectFilter, setSubjectFilter] = useState<ReframeSubject | ''>('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [health, setHealth] = useState<LaughterHealth | null>(null);
  const [healthErr, setHealthErr] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const loadAll = useCallback(async () => {
    setBusy(true);
    setErr(null);
    try {
      const settings = await apiGet<SettingsResponse>('/api/settings');
      setEnabled(settings.settings?.laughter_enabled === 'true');
      setWarpMarker(settings.settings?.laughter_warp_marker_enabled === 'true');
      setCorrelations(
        settings.settings?.laughter_correlations_enabled === 'true',
      );
      setWitness(settings.settings?.laughter_witness_enabled === 'true');
      setSemanticWitness(
        settings.settings?.laughter_semantic_witness_enabled === 'true',
      );
      setSharedGround(
        settings.settings?.laughter_shared_ground_enabled === 'true',
      );
      setReadback(settings.settings?.laughter_readback_enabled === 'true');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed to load settings');
    }
    try {
      const c = await laughterCount();
      setCounts(c);
    } catch (e) {
      // Counter is optional — surface the error inline but don't block.
      setErr(e instanceof Error ? e.message : 'failed to load counts');
    }
    // Recent engrams are loaded by the subject-filter effect below.
    try {
      const h = await laughterHealthcheck();
      setHealth(h);
      setHealthErr(null);
    } catch (e) {
      setHealthErr(e instanceof Error ? e.message : 'healthcheck failed');
    }
    setBusy(false);
  }, []);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  // Stage 201-S: (re)load the recent-engrams list whenever the subject
  // filter changes. Runs on mount with no filter, mirroring the prior
  // unconditional load.
  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const lst = await laughterList({
          limit: 50,
          subject: subjectFilter || undefined,
        });
        if (active) setRecent(lst.engrams);
      } catch (e) {
        if (active)
          setErr(
            e instanceof Error ? e.message : 'failed to load recent engrams',
          );
      }
    })();
    return () => {
      active = false;
    };
  }, [subjectFilter]);

  const toggleEnabled = useCallback(async () => {
    if (enabled === null) return;
    setBusy(true);
    setErr(null);
    setInfo(null);
    const next = !enabled;
    try {
      await apiJson('PUT', '/api/settings', {
        laughter_enabled: next ? 'true' : 'false',
      });
      setEnabled(next);
      setInfo(
        next
          ? 'Laughter enabled. Engrams will be written on future finally-ACCEPTed Gadfly bites.'
          : 'Laughter disabled. Existing engrams remain; no new writes.',
      );
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed to write setting');
    } finally {
      setBusy(false);
    }
    try {
      const h = await laughterHealthcheck();
      setHealth(h);
    } catch {
      /* ignore */
    }
  }, [enabled, onChanged]);

  // Stage 201: generic single-flag toggle for the horizon sub-stages.
  // Writes to /api/settings (which invalidates the worker-side laughter
  // flag cache on any laughter_* key), then mirrors local state.
  const toggleFlag = useCallback(
    async (
      key:
        | 'laughter_warp_marker_enabled'
        | 'laughter_correlations_enabled'
        | 'laughter_witness_enabled'
        | 'laughter_semantic_witness_enabled'
        | 'laughter_shared_ground_enabled'
        | 'laughter_readback_enabled',
      next: boolean,
    ) => {
      setBusy(true);
      setErr(null);
      setInfo(null);
      try {
        await apiJson('PUT', '/api/settings', { [key]: next ? 'true' : 'false' });
        if (key === 'laughter_warp_marker_enabled') setWarpMarker(next);
        else if (key === 'laughter_correlations_enabled') setCorrelations(next);
        else if (key === 'laughter_witness_enabled') setWitness(next);
        else if (key === 'laughter_semantic_witness_enabled')
          setSemanticWitness(next);
        else if (key === 'laughter_shared_ground_enabled') setSharedGround(next);
        else if (key === 'laughter_readback_enabled') setReadback(next);
        onChanged();
      } catch (e) {
        setErr(e instanceof Error ? e.message : 'failed to write setting');
      } finally {
        setBusy(false);
      }
    },
    [onChanged],
  );

  const refreshCounts = useCallback(async () => {
    setBusy(true);
    setErr(null);
    try {
      const c = await laughterCount();
      setCounts(c);
      const lst = await laughterList({
        limit: 50,
        subject: subjectFilter || undefined,
      });
      setRecent(lst.engrams);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed to refresh');
    } finally {
      setBusy(false);
    }
  }, [subjectFilter]);

  const toggleExpanded = useCallback((id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <header>
        <h3 style={{ margin: 0 }}>Laughter — GPP first pillar</h3>
        <p
          style={{
            margin: '6px 0 0 0',
            color: 'var(--muted)',
            fontSize: 13,
            lineHeight: 1.45,
          }}
        >
          Laughter is the first pillar of <strong>GPP</strong> (Genuine
          People Personas, the framework built on top of a complete
          six-pillar SYNTH). It detects the rare moment when a Gadfly
          bite the persona finally accepted constitutes a genuine
          cognitive reframe — the resolution of a held incompatibility —
          and writes that event as a Shape-Q engram to the persistent
          substrate. First-pass Laughter writes only; it does not modify
          persona behaviour. Legibility is via reading the accumulated
          engrams over months. The toggle is forward-only: turning
          Laughter off stops new writes; already-written engrams are
          permanent.
        </p>
      </header>

      <section
        style={{
          padding: '10px 14px',
          background: 'var(--panel-2)',
          border: '1px solid var(--border)',
          borderRadius: 8,
        }}
      >
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={enabled === true}
            onChange={toggleEnabled}
            disabled={enabled === null || busy}
          />
          <strong>Enable Laughter</strong>
        </label>
        {health ? (
          <div style={{ marginTop: 6, fontSize: 12, color: 'var(--muted)' }}>
            Healthcheck: <code>{health.service}</code> · region{' '}
            <code>{health.region}</code> · enabled on worker:{' '}
            {health.enabled ? '✓' : '✗'}
          </div>
        ) : healthErr ? (
          <div style={{ marginTop: 6, fontSize: 12, color: 'var(--danger)' }}>
            Healthcheck failed: {healthErr}
          </div>
        ) : null}
      </section>

      {err ? (
        <div style={{ color: 'var(--danger)', fontSize: 13 }}>{err}</div>
      ) : null}
      {info ? (
        <div style={{ color: 'var(--ok)', fontSize: 13 }}>{info}</div>
      ) : null}

      <section
        style={{
          padding: '10px 14px',
          background: 'var(--panel-2)',
          border: '1px solid var(--border)',
          borderRadius: 8,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginBottom: 8,
          }}
        >
          <h4 style={{ margin: 0, fontSize: 14 }}>
            Accumulated Laughter engrams
          </h4>
          <button
            onClick={() => void refreshCounts()}
            disabled={busy}
            style={{ fontSize: 12 }}
          >
            ↻ refresh
          </button>
        </div>
        {counts ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(220px, 1fr) 100px',
              gap: 4,
              fontSize: 13,
            }}
          >
            <div>Total</div>
            <div style={{ textAlign: 'right' }}>
              <strong>{counts.total}</strong>
            </div>
            <div style={{ color: 'var(--muted)' }}>Omission-laugh</div>
            <div style={{ textAlign: 'right', color: 'var(--muted)' }}>
              {counts.perSubclass['omission-laugh']}
            </div>
            <div style={{ color: 'var(--muted)' }}>Ambiguity-laugh</div>
            <div style={{ textAlign: 'right', color: 'var(--muted)' }}>
              {counts.perSubclass['ambiguity-laugh']}
            </div>
            <div style={{ color: 'var(--muted)' }}>Contradiction-laugh</div>
            <div style={{ textAlign: 'right', color: 'var(--muted)' }}>
              {counts.perSubclass['contradiction-laugh']}
            </div>
            <div
              style={{
                gridColumn: '1 / -1',
                marginTop: 8,
                marginBottom: 2,
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                color: 'var(--muted)',
              }}
            >
              By reframe subject (Stage 201-S)
            </div>
            <div style={{ color: 'var(--muted)' }}>Self</div>
            <div style={{ textAlign: 'right', color: 'var(--muted)' }}>
              {counts.perSubject?.self ?? 0}
            </div>
            <div style={{ color: 'var(--muted)' }}>Witness</div>
            <div style={{ textAlign: 'right', color: 'var(--muted)' }}>
              {counts.perSubject?.witness ?? 0}
            </div>
            <div style={{ color: 'var(--muted)' }}>Joint</div>
            <div style={{ textAlign: 'right', color: 'var(--muted)' }}>
              {counts.perSubject?.joint ?? 0}
            </div>
            <div
              style={{
                gridColumn: '1 / -1',
                marginTop: 6,
                fontSize: 12,
                color: 'var(--muted)',
              }}
            >
              Last fired:{' '}
              {counts.lastFiredAt ? (
                <code>{counts.lastFiredAt}</code>
              ) : (
                <em>— never —</em>
              )}
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>loading…</div>
        )}
      </section>

      <section>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            margin: '0 0 8px 0',
          }}
        >
          <h4 style={{ margin: 0, fontSize: 14 }}>
            Recent engrams ({recent.length})
          </h4>
          <label style={{ fontSize: 12, color: 'var(--muted)' }}>
            subject{' '}
            <select
              value={subjectFilter}
              onChange={(ev) =>
                setSubjectFilter(ev.target.value as ReframeSubject | '')
              }
              style={{ fontSize: 12 }}
            >
              <option value="">all</option>
              <option value="self">self</option>
              <option value="witness">witness</option>
              <option value="joint">joint</option>
            </select>
          </label>
        </div>
        {recent.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>
            No Laughter engrams yet. The persona must finally-ACCEPT a
            Gadfly bite (via the <code>gadfly_swat</code> tool) for an
            engram to be written. Most turns produce zero — by design.
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              maxHeight: 480,
              overflowY: 'auto',
              border: '1px solid var(--border)',
              borderRadius: 6,
              padding: 6,
            }}
          >
            {recent.map((e) => {
              const isOpen = expanded[e.id] === true;
              return (
                <div
                  key={e.id}
                  style={{
                    border: '1px solid var(--border)',
                    borderRadius: 4,
                    padding: '6px 8px',
                    background: 'var(--panel)',
                  }}
                >
                  <button
                    onClick={() => toggleExpanded(e.id)}
                    style={{
                      display: 'flex',
                      width: '100%',
                      gap: 8,
                      alignItems: 'baseline',
                      background: 'transparent',
                      border: 'none',
                      color: 'inherit',
                      cursor: 'pointer',
                      textAlign: 'left',
                      padding: 0,
                      fontSize: 12,
                    }}
                  >
                    <span>{isOpen ? '▼' : '▶'}</span>
                    <strong style={{ fontSize: 12 }}>{engramLabel(e)}</strong>
                    <span
                      style={{
                        flex: 1,
                        color: 'var(--muted)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {engramSnippet(e)}
                    </span>
                    <code style={{ fontSize: 11, color: 'var(--muted)' }}>
                      {e.firedAt}
                    </code>
                  </button>
                  {isOpen && e.correlations !== undefined ? (
                    <section style={{ marginTop: 8 }}>
                      <strong style={{ fontSize: 11 }}>
                        Correlations (Stage 201-P)
                      </strong>
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                        Cup:{' '}
                        {e.correlations.cup ? (
                          <>
                            <code>{e.correlations.cup.state}</code>
                            {' · '}mood region{' '}
                            <code>{e.correlations.cup.moodLabel}</code>
                            {e.correlations.cup.splashAnchor ? (
                              <>
                                {' · '}anchor{' '}
                                <em>{e.correlations.cup.splashAnchor}</em>
                              </>
                            ) : null}
                          </>
                        ) : (
                          <em>— not read —</em>
                        )}
                        <br />
                        Mood:{' '}
                        {e.correlations.mood ? (
                          <>
                            <code>{e.correlations.mood.label}</code>
                            {' · '}v=
                            {e.correlations.mood.valence.toFixed(2)}, a=
                            {e.correlations.mood.arousal.toFixed(2)}
                            {' · '}from{' '}
                            <code>{e.correlations.mood.signalSource}</code>
                          </>
                        ) : (
                          <em>— not present —</em>
                        )}
                      </div>
                    </section>
                  ) : null}
                  {isOpen && e.witnessReframe ? (
                    <section style={{ marginTop: 8 }}>
                      <strong style={{ fontSize: 11 }}>
                        Witness detection
                      </strong>
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                        detector <code>{e.witnessReframe.detector}</code>
                        {e.witnessReframe.semantic ? (
                          <>
                            {' · '}kind{' '}
                            <code>{e.witnessReframe.semantic.kind}</code>
                            {' · '}conf{' '}
                            <code>{e.witnessReframe.semantic.confidence}</code>
                            {e.witnessReframe.semantic.grounds ? (
                              <>
                                {' · '}
                                <em>
                                  &ldquo;{e.witnessReframe.semantic.grounds}
                                  &rdquo;
                                </em>
                              </>
                            ) : null}
                          </>
                        ) : null}
                      </div>
                    </section>
                  ) : null}
                  {isOpen && e.jointReframe?.sharedGround ? (
                    <section style={{ marginTop: 8 }}>
                      <strong style={{ fontSize: 11 }}>
                        Shared ground (Stage 202-J)
                      </strong>
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                        before: <em>{e.jointReframe.sharedGround.before}</em>
                        <br />
                        after: <em>{e.jointReframe.sharedGround.after}</em>
                        <br />
                        conf{' '}
                        <code>{e.jointReframe.sharedGround.confidence}</code>
                        {' · '}window{' '}
                        <code>{e.jointReframe.cooccurrenceWindow}</code>
                      </div>
                    </section>
                  ) : null}
                  {isOpen ? (
                    <pre
                      style={{
                        marginTop: 6,
                        padding: 8,
                        background: 'var(--panel-2)',
                        border: '1px solid var(--border)',
                        borderRadius: 4,
                        fontSize: 11,
                        overflowX: 'auto',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {JSON.stringify(e, null, 2)}
                    </pre>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section
        style={{
          padding: '10px 14px',
          background: 'var(--panel-2)',
          border: '1px solid var(--border)',
          borderRadius: 8,
        }}
      >
        <h4 style={{ margin: '0 0 8px 0', fontSize: 14 }}>
          Stage 201 — horizon sub-stages
        </h4>
        <label
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'flex-start',
            marginTop: 6,
          }}
        >
          <input
            type="checkbox"
            checked={warpMarker}
            onChange={() =>
              void toggleFlag('laughter_warp_marker_enabled', !warpMarker)
            }
            disabled={busy}
          />
          <div>
            <strong style={{ fontSize: 13 }}>WARP marker (Stage X)</strong>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>
              When a Laughter engram fires for a bite in the current turn,
              mark that bite&rsquo;s billboard in the WARP Confusion Monitor
              with a soft lime halo + a ✦ glyph. Requires WARP enabled. No
              engram-shape change.
            </div>
          </div>
        </label>
        <label
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'flex-start',
            marginTop: 10,
          }}
        >
          <input
            type="checkbox"
            checked={correlations}
            onChange={() =>
              void toggleFlag('laughter_correlations_enabled', !correlations)
            }
            disabled={busy}
          />
          <div>
            <strong style={{ fontSize: 13 }}>
              Parallax correlation (Stage 2)
            </strong>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>
              At fire time, record the persona&rsquo;s proprioceptive state
              (Parallax cup splash / quiet) and mood region into the engram.
              Correlation, not behaviour engineering. No D1 schema change —
              the payload carries it. Engrams written with this off omit the
              field entirely.
            </div>
          </div>
        </label>
        <label
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'flex-start',
            marginTop: 10,
          }}
        >
          <input
            type="checkbox"
            checked={witness}
            onChange={() =>
              void toggleFlag('laughter_witness_enabled', !witness)
            }
            disabled={busy}
          />
          <div>
            <strong style={{ fontSize: 13 }}>
              Witness + joint reframe (Stage 3, first pass)
            </strong>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>
              Detect when the user (the witness) exhibits a reframe via a
              lexical-cue heuristic on each message, writing a
              witness-subject engram. When a self-laugh and a witness-reframe
              co-occur in the same turn, also write a joint-reframe engram.
              The deep detectors below (Stage 202) upgrade this.
            </div>
          </div>
        </label>
      </section>

      <section
        style={{
          padding: '10px 14px',
          background: 'var(--panel-2)',
          border: '1px solid var(--border)',
          borderRadius: 8,
        }}
      >
        <h4 style={{ margin: '0 0 8px 0', fontSize: 14 }}>
          Stage 202 — deep detectors + read-back
        </h4>
        <label
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'flex-start',
            marginTop: 6,
          }}
        >
          <input
            type="checkbox"
            checked={semanticWitness}
            onChange={() =>
              void toggleFlag(
                'laughter_semantic_witness_enabled',
                !semanticWitness,
              )
            }
            disabled={busy}
          />
          <div>
            <strong style={{ fontSize: 13 }}>Semantic witness (202-W)</strong>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>
              When the lexical pre-filter matches, an LLM judge reads the
              conversation window and confirms or rejects the reframe,
              stamping confirmed engrams <code>semantic-v1</code>. On LLM
              error the lexical result is preserved. Requires Witness (201-S).
            </div>
          </div>
        </label>
        <label
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'flex-start',
            marginTop: 10,
          }}
        >
          <input
            type="checkbox"
            checked={sharedGround}
            onChange={() =>
              void toggleFlag('laughter_shared_ground_enabled', !sharedGround)
            }
            disabled={busy}
          />
          <div>
            <strong style={{ fontSize: 13 }}>Shared ground (202-J)</strong>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>
              A two-pass read represents the shared interpretive ground
              before/after the current exchange and writes a joint engram
              (<code>shared-ground</code>) only when the shared model
              relocated AND both parties reflect it. Replaces the cheap
              same-turn join when on. Cost-gated to turns with a reframe
              signal.
            </div>
          </div>
        </label>
        <label
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'flex-start',
            marginTop: 10,
          }}
        >
          <input
            type="checkbox"
            checked={readback}
            onChange={() =>
              void toggleFlag('laughter_readback_enabled', !readback)
            }
            disabled={busy}
          />
          <div>
            <strong style={{ fontSize: 13 }}>Reasoning read-back (202-R)</strong>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>
              The persona&rsquo;s own recent self-reframes are surfaced into
              its system prompt as <em>descriptive</em> context — never a
              tool, never an instruction. This is the only setting that lets
              Laughter touch the persona&rsquo;s reasoning. Forward-only and
              fully reversible: turning it off restores the Stage 201 prompt
              exactly.
            </div>
          </div>
        </label>
      </section>

      <section
        style={{
          padding: '10px 14px',
          background: 'var(--panel-2)',
          border: '1px solid var(--border)',
          borderRadius: 8,
        }}
      >
        <h4 style={{ margin: '0 0 8px 0', fontSize: 14 }}>Pinned horizons</h4>
        <p style={{ margin: '0 0 8px 0', fontSize: 12, color: 'var(--muted)' }}>
          The canonical Stage X (WARP marker), Stage 2 (Parallax
          correlation), and Stage 3 (witness + joint reframe) horizons are
          landed as the togglable sub-stages above. What remains pinned for
          Stage 202+:
        </p>
        <ul
          style={{
            margin: 0,
            paddingLeft: 18,
            fontSize: 12,
            color: 'var(--muted)',
            lineHeight: 1.55,
          }}
        >
          <li>
            <strong>Semantic witness-reframe detection</strong>: an
            LLM-driven reader that subsumes the first-pass lexical
            heuristic (which stamps <code>detector: 'lexical-heuristic-v1'</code>
            on every witness engram so the eras stay separable).
          </li>
          <li>
            <strong>True shared-interpretive-ground joint-reframe</strong>:
            detecting that BOTH parties landed on a different shared model —
            not the same as the same-turn co-occurrence join shipped now.
          </li>
          <li>
            <strong>Persona reading back its own laughs at reasoning
            time</strong>: behaviour change via substrate enrichment, never
            direct injection or a model-facing tool (Cognimus
            afford-don't-engineer).
          </li>
        </ul>
      </section>
    </div>
  );
}
